import { useEffect, useState } from "react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Trash2, Archive as ArchiveIcon, ExternalLink, Download as DownloadIcon, ChevronUp, ChevronDown } from "lucide-react";

const PROJECTS = [
    { id: "ruda", label: "Project Ruda Śląska" },
    { id: "branszczyk", label: "Project Gmina Brańszczyk" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function ArchiveManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [reordering, setReordering] = useState(false);

    const [draft, setDraft] = useState({
        project: "ruda",
        version: "",
        game_version: "",
        file_size: "-",
        release_date: today(),
        changelog: "",
        download_url: "",
        download_type: "external",
        order: 0,
    });
    const [file, setFile] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/archive");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const onAdd = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const fd = new FormData();
            Object.entries(draft).forEach(([k, v]) => fd.append(k, v ?? ""));
            if (file) fd.append("file", file);
            else if (!draft.download_url) {
                setError("Podaj URL lub wybierz plik.");
                setSubmitting(false);
                return;
            }
            await api.post("/archive", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDraft({
                project: draft.project,
                version: "",
                game_version: "",
                file_size: "-",
                release_date: today(),
                changelog: "",
                download_url: "",
                download_type: "external",
                order: 0,
            });
            setFile(null);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć wersję z archiwum?")) return;
        try {
            await api.delete(`/archive/${id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    // Move an item up/down and persist the new positions (renumbers order 0..n-1).
    const move = async (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= items.length || reordering) return;
        const next = [...items];
        const [moved] = next.splice(index, 1);
        next.splice(target, 0, moved);
        setItems(next); // optimistic reorder
        setReordering(true);
        setError("");
        try {
            const updates = next
                .map((it, i) => (it.order !== i ? api.put(`/archive/${it.id}`, { order: i }) : null))
                .filter(Boolean);
            await Promise.all(updates);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
            await load();
        } finally {
            setReordering(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-archive">
            <form
                onSubmit={onAdd}
                data-testid="archive-form"
                className="panel-yellow p-6 lg:col-span-5 h-fit"
            >
                <div className="label-eyebrow mb-3">Dodaj archiwalną wersję</div>

                <Field label="Projekt">
                    <select
                        data-testid="archive-project-select"
                        value={draft.project}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, project: e.target.value }))
                        }
                        className="input"
                    >
                        {PROJECTS.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Wersja *">
                        <input
                            data-testid="archive-version-input"
                            value={draft.version}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, version: e.target.value }))
                            }
                            placeholder="0.8.0"
                            required
                            className="input"
                        />
                    </Field>
                    <Field label="Data wydania">
                        <input
                            type="date"
                            data-testid="archive-date-input"
                            value={draft.release_date}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, release_date: e.target.value }))
                            }
                            className="input"
                        />
                    </Field>
                </div>

                <Field label="Kompatybilna wersja gry (ETS2)">
                    <input
                        data-testid="archive-gameversion-input"
                        value={draft.game_version}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, game_version: e.target.value }))
                        }
                        placeholder="np. 1.49.x, 1.50, 1.50–1.52"
                        className="input"
                    />
                </Field>

                <Field label="Kolejność (niższa = wyżej na liście)">
                    <input
                        type="number"
                        data-testid="archive-order-input"
                        value={draft.order}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, order: Number(e.target.value) || 0 }))
                        }
                        className="input"
                    />
                </Field>

                <Field label="Rozmiar (auto z pliku)">
                    <input
                        data-testid="archive-size-input"
                        value={draft.file_size}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, file_size: e.target.value }))
                        }
                        className="input"
                    />
                </Field>

                <Field label="Changelog">
                    <textarea
                        data-testid="archive-changelog-input"
                        value={draft.changelog}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, changelog: e.target.value }))
                        }
                        rows={4}
                        className="input"
                    />
                </Field>

                <Field label="Typ pobierania">
                    <select
                        data-testid="archive-type-select"
                        value={draft.download_type}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, download_type: e.target.value }))
                        }
                        className="input"
                    >
                        <option value="external">Link zewnętrzny</option>
                        <option value="file">Plik na serwerze</option>
                    </select>
                </Field>

                <Field label="URL (alternatywa do pliku)">
                    <input
                        type="url"
                        data-testid="archive-url-input"
                        value={draft.download_url}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, download_url: e.target.value }))
                        }
                        placeholder="https://..."
                        className="input"
                    />
                </Field>

                <Field label="Lub prześlij plik (.zip, .scs)">
                    <input
                        type="file"
                        data-testid="archive-file-input"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                    />
                </Field>

                {error && (
                    <div data-testid="archive-error" className="text-sm text-red-400 mb-3">
                        {error}
                    </div>
                )}

                <button
                    data-testid="archive-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-2 disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <Plus size={14} />
                    )}
                    Dodaj do archiwum
                </button>
                <style>{`
                    .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                    .input:focus { outline:none; border-color: var(--primary); }
                `}</style>
            </form>

            <div className="lg:col-span-7">
                <div className="label-eyebrow mb-4">Wszystkie wersje ({items.length})</div>
                {loading ? (
                    <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
                ) : items.length === 0 ? (
                    <div className="panel-yellow p-8 text-center text-[color:var(--text-secondary)] text-sm">
                        <ArchiveIcon size={24} className="mx-auto mb-3 text-[color:var(--primary)]" />
                        Brak wersji w archiwum.
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {items.map((v, idx) => {
                            const isExt = v.download_type === "external";
                            const isGreen = v.project === "branszczyk";
                            const accent = isGreen ? "#34d399" : "#facc15";
                            return (
                                <li
                                    key={v.id}
                                    data-testid={`archive-row-${v.id}`}
                                    className="panel-yellow p-4 flex items-center gap-4"
                                    style={{ borderColor: `${accent}40` }}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full"
                                                style={{
                                                    background: `${accent}1a`,
                                                    color: accent,
                                                }}
                                            >
                                                {PROJECTS.find((p) => p.id === v.project)?.label ||
                                                    v.project}
                                            </span>
                                            <span className="font-display text-lg text-white">
                                                v{v.version}
                                            </span>
                                            {v.game_version && (
                                                <span
                                                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
                                                    style={{
                                                        borderColor: `${accent}40`,
                                                        color: "var(--text-secondary)",
                                                    }}
                                                >
                                                    ETS2 {v.game_version}
                                                </span>
                                            )}
                                            <span className="text-xs text-[color:var(--text-muted)]">
                                                · {v.file_size || "-"}
                                            </span>
                                            {v.release_date && (
                                                <span className="text-xs text-[color:var(--text-muted)]">
                                                    · {v.release_date}
                                                </span>
                                            )}
                                        </div>
                                        {v.changelog && (
                                            <div className="text-xs text-[color:var(--text-secondary)] mt-1 line-clamp-2 whitespace-pre-wrap">
                                                {v.changelog}
                                            </div>
                                        )}
                                    </div>
                                    {v.download_url && (
                                        <a
                                            href={resolveMediaUrl(v.download_url)}
                                            target={isExt ? "_blank" : undefined}
                                            rel={isExt ? "noopener noreferrer" : undefined}
                                            className="text-xs uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1"
                                        >
                                            <DownloadIcon size={14} />
                                            {isExt && <ExternalLink size={12} />}
                                        </a>
                                    )}
                                    <div className="flex flex-col">
                                        <button
                                            type="button"
                                            data-testid={`archive-up-${v.id}`}
                                            onClick={() => move(idx, -1)}
                                            disabled={idx === 0 || reordering}
                                            className="text-[color:var(--text-secondary)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                                            aria-label="Przesuń w górę"
                                        >
                                            <ChevronUp size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`archive-down-${v.id}`}
                                            onClick={() => move(idx, 1)}
                                            disabled={idx === items.length - 1 || reordering}
                                            className="text-[color:var(--text-secondary)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-0.5"
                                            aria-label="Przesuń w dół"
                                        >
                                            <ChevronDown size={16} />
                                        </button>
                                    </div>
                                    <button
                                        data-testid={`archive-delete-${v.id}`}
                                        onClick={() => onDelete(v.id)}
                                        className="text-red-400 hover:text-red-300 p-2"
                                        aria-label="Usuń"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
